import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { Schema } from '@rossum/api-client/schemas';
import { Workspace } from '@rossum/api-client/workspaces';
import {
  Alert,
  Button,
  DialogActions,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { Queue } from '../../types/queue';
import { getPartId } from '../create-field/helpers';
import { createWorkspaceOptions } from '../helpers';
import { Dialog } from '../ui/Dialog';
import { SelectSchemaLocation } from '../ui/SelectSchemaLocation';

type AddFieldDialogProps = {
  open: boolean;
  onClose: () => void;
  workspaces: Workspace[];
  queues: Queue[];
  schemas: Schema[];
};

const AddFieldDialog = ({
  open,
  onClose,
  workspaces,
  queues,
  schemas,
}: AddFieldDialogProps) => {
  const { push } = useHistory();
  const intl = useIntl();
  const { pathname } = useLocation();

  // Location list
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const workspacesWithQueuesAndSchemas = useMemo(
    () => createWorkspaceOptions(workspaces, queues, schemas),
    [workspaces, queues, schemas]
  );

  const onSubmitDialog = useCallback(
    (selectedItem: string) => {
      const parentId =
        getPartId(selectedItem, 'multivalueId') ??
        getPartId(selectedItem, 'sectionId');
      const schemaId = getPartId(selectedItem, 'schemaId');
      push(`${pathname}/schema/${schemaId}/parent/${parentId}/create-field`);
    },
    [pathname, push]
  );

  return (
    <Dialog
      onClose={onClose}
      open={open}
      title={intl.formatMessage({
        id: 'features.fieldManager.overview.addFieldDialog.title',
      })}
      actions={
        <DialogActions>
          <Button
            variant="outlined"
            onClick={onClose}
            color="secondary"
            data-cy="fm-select-location-cancel-btn"
          >
            {intl.formatMessage({
              id: 'features.fieldManager.overview.addFieldDialog.button.cancel',
            })}
          </Button>
          <Button
            variant="contained"
            data-cy="fm-select-location-submit-btn"
            disabled={selectedItems.length === 0}
            onClick={() => {
              if (selectedItems[0]) {
                onSubmitDialog(selectedItems[0]);
              }
            }}
          >
            {intl.formatMessage({
              id: 'features.fieldManager.overview.addFieldDialog.button.selectLocation',
            })}
          </Button>
        </DialogActions>
      }
    >
      <Stack spacing={2}>
        {workspacesWithQueuesAndSchemas.length > 0 ? (
          <Typography variant="body2">
            {intl.formatMessage({
              id: 'features.fieldManager.overview.addFieldDialog.description',
            })}
          </Typography>
        ) : null}
        <Stack sx={{ maxHeight: 'min(600px, 40vh)', overflow: 'auto' }}>
          {workspacesWithQueuesAndSchemas.length > 0 ? (
            <SelectSchemaLocation
              options={workspacesWithQueuesAndSchemas}
              mode="single-select"
              onSelect={ids => setSelectedItems(ids)}
              selectedOptions={selectedItems}
              expandedOptions={expandedItems}
              setExpandedOptions={setExpandedItems}
            />
          ) : (
            <Alert severity="warning" variant="outlined">
              {intl.formatMessage({
                id: 'features.fieldManager.overview.addFieldDialog.noData',
              })}
            </Alert>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
};

export { AddFieldDialog };
